import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Label, Button, Icon } from 'semantic-ui-react';
import i18next from 'i18next';

import { DataGrid } from '../../common';
import SideEditor from '../../general/SideEditor';
import RestaurantSetupEditor from './RestaurantSetupEditor';
import RestaurantSettingsEditor from './RestaurantSettingsEditor';
import SupportedOrderOptions from './SupportedOrderOptions';
import { getFullRestaurantsData } from '../../../selectors';
import { fetchArchivedRestaurants, setEditedEntity, confirmDelete, deleteRestaurant } from '../../../actions';
import { hasBusinessPermission } from '../../../helpers/permissions';
import { updateUrlState } from '../../../helpers/url';
import { combineRestaurantsData } from '../../common/DataGrid/filters/helper';

import './Restaurants.scss';

class Restaurants extends Component {
    state = {
        archivedRestaurants: []
    }

    constructor(props) {
        super(props);

        this.renderRestaurantsActions = this.renderRestaurantsActions.bind(this);
        this.renderRestaurantsInfo = this.renderRestaurantsInfo.bind(this);
    }

    componentDidMount() {
        if (hasBusinessPermission('operation') && this.props.deletedOnly) {
            this.props.fetchArchivedRestaurants([], restaurants => {
                restaurants = restaurants.filter(rest => this.props.match.params.chainId ? rest.ownerId === this.props.match.params.chainId: rest) || {};
                this.setState({ archivedRestaurants: combineRestaurantsData(restaurants, this.props.posSuppliers) });
            });
        }

        updateUrlState(this.props.history, this.props.history.location, { sortCriteria: 'chain', sortDirection: 'asc' });
    }

    handleDelete = (rest) => {
        this.props.confirmDelete(rest.name, () => this.props.deleteRestaurant(rest.id));
    }

    figureRestaurantStatus(rest) {
        if (typeof rest !== "undefined") {
            if (rest.isTerminated) {
                return 'terminated';
            } else if (rest.isSuspended) {
                return 'suspended';
            } else if (rest.missingPosBranchId) {
                return 'error';
            } else if (!rest.inactive || rest.activeKiosks > 0) {
                return 'active';
            }
        }
        return 'inactive';
    }

    figureTagColor(type) {
        const tagType = type.toLowerCase();
        switch (tagType) {
            case 'category':
                return 'blue';
            case 'ethnic':
                return 'orange';
            case 'lifestyle':
                return 'teal';
            case 'price':
                return 'green';
            default:
                return 'grey';
        }
    }

    renderRestaurantsInfo() {
        const { restaurants } = this.props;

        return (
            <span style={{ marginLeft: 10, marginRight: 10 }}>
                {i18next.t('restaurantsSummery', {
                    total: restaurants.length,
                    active: restaurants.filter(rest => !rest.inactive).length,
                })}
            </span>
        );
    }

    renderRestaurantsActions() {
        if (!hasBusinessPermission('operation')) return null;

        const { setEditedEntity } = this.props;

        return (
            <div>
                <Button
                    icon
                    labelPosition='left'
                    floated="right"
                    primary
                    onClick={() => setEditedEntity('restaurant')}
                >
                    <Icon name='add' /> {i18next.t('create')}
                </Button>
            </div>
        );
    }

    render() {
        const { setEditedEntity, restaurants, history, editedType, chains } = this.props;

        const supportOptions = {
            supportCartOrderTable: 'sitting',
            supportCartOrderPickup: 'takeaway',
            supportCartOrderDelivery: 'deliveries',
            supportQuickPayment: 'quickPay',
            supportTableOrderCode: 'tableNumber',
            supportKiosk: 'kiosk'
        };

        const columns = [{
            id: 'id',
            Header: '#',
            maxWidth: 75,
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center' },
            accessor: rest => parseInt(rest.id),
            filterable: true,
            Cell: row => {
                const rest = row.original;

                return (
                    <span>
                        {rest.id}
                        {rest.missingPosBranchId ? <Icon name="warning" /> : null}
                    </span>
                );
            }
        }, {
            Header: i18next.t('name'),
            accessor: 'name',
            filterable: true
        }, {
            id: 'chain',
            Header: i18next.t('chain.name'),
            accessor: rest => {
                const chain = chains && chains.find(chain => chain.id === rest.ownerId);

                return chain ? chain.name : '';
            },
            filterable: true
        }, {
            id: 'activityStatus',
            Header: i18next.t('activityStatus'),
            maxWidth: 100,
            accessor: rest => this.figureRestaurantStatus(rest),
            Cell: row => {
                const activityStatus = this.figureRestaurantStatus(row.original);
                let statusColor = 'yellow';
                if (activityStatus === 'terminated') {
                    statusColor = 'grey';
                } else if (activityStatus === 'suspended') {
                    statusColor = 'blue';
                } else if (activityStatus === 'error') {
                    statusColor = 'red';
                } else if (activityStatus === 'active') {
                    statusColor = 'green';
                }

                return (
                    <div>
                        <Label circular color={statusColor} empty className='pi-restaurant-activity-status-label' />
                        <span className='pi-restaurant-activity-status-text'>{i18next.t(row.value)}</span>
                    </div>
                );
            },
            filterable: true,
            filterValues: [
                { value: 'active', text: i18next.t('active') },
                { value: 'inactive', text: i18next.t('inactive') },
                { value: 'error', text: i18next.t('error') },
                { value: 'terminated', text: i18next.t('terminated') },
                { value: 'suspended', text: i18next.t('suspended') }
            ]
        }, {
            id: 'orderTypesOptions',
            Header: i18next.t('supportedOrderTypes'),
            maxWidth: 200,
            accessor: rest =>
                Object.keys(supportOptions).map((support, index) => {
                    if (rest[support]) return supportOptions[support];
                }).filter(support => !!support).join(','),
            Cell: row =>
                <SupportedOrderOptions restaurant={row.original} />,
            sortable: false,
            filterable: true,
            filterChoices: [
                { value: 'sitting', text: i18next.t('sitting') },
                { value: 'takeaway', text: i18next.t('takeaway') },
                { value: 'deliveries', text: i18next.t('deliveries') },
                { value: 'quickPay', text: i18next.t('quickPay') },
                { value: 'tableNumber', text: i18next.t('tableNumber') },
                { value: 'kiosk', text: i18next.t('kiosk') }
            ]
        }, {
            Header: i18next.t('posSupplier'),
            accessor: 'posSupplier',
            maxWidth: 100,
            filterable: true
        }, {
            id: 'actions',
            Header: i18next.t('actions'),
            minWidth: 176,
            maxWidth: 176,
            sortable: false,
            filterable: false,
            Cell: row => {
                const rest = row.original;

                return (
                    <div>
                        <Button
                            circular
                            size='tiny'
                            icon='find'
                            primary
                            onClick={() => history.push(`/restaurant/${rest.id}`)}
                        />
                        {hasBusinessPermission('operation') ? <Button circular size='tiny' icon='write' basic onClick={() => setEditedEntity('restaurant', rest)} /> : null}
                        <Button circular size='tiny' icon='setting' color='yellow' onClick={() => setEditedEntity('restaurantConfig', rest)} />
                        <Button circular size='tiny' icon='tablet alternate' color='pink' onClick={() => history.push(`/restaurant/${rest.id}/kiosks`)} />
                        {hasBusinessPermission('operation') ? <Button circular size='tiny' icon='trash' negative onClick={() => this.handleDelete(rest)} /> : null}
                    </div>
                );
            }
        }];

        return (
            <div style={{ padding: 10 }}>
                <SideEditor
                    title={i18next.t('restaurant')}
                    visible={editedType === 'restaurant'}
                    handleClose={() => setEditedEntity(null)}
                >
                    <RestaurantSetupEditor />
                </SideEditor>
                <SideEditor
                    title={i18next.t('restaurant')}
                    visible={editedType === 'restaurantConfig'}
                    handleClose={() => setEditedEntity(null)}
                >
                    <RestaurantSettingsEditor />
                </SideEditor>

                <DataGrid
                    data={this.state.archivedRestaurants.length > 0 ? this.state.archivedRestaurants : restaurants}
                    columns={columns}
                    getTrGroupProps={(state, rowInfo, column) => {
                        if (typeof rowInfo !== "undefined") {
                            const restaurantStatus = this.figureRestaurantStatus(rowInfo.original);

                            if (restaurantStatus === 'terminated') {
                                return { className: 'pi-data-grid-row-terminated' };
                            } else if (restaurantStatus === 'suspended') {
                                return { className: 'pi-data-grid-row-suspended' };
                            } else if (restaurantStatus === 'error') {
                                return {
                                    className: 'pi-data-grid-row-negative',
                                    style: { fontWeight: 'bold' }
                                };
                            } else if (restaurantStatus === 'active') {
                                return { className: 'pi-data-grid-row-positive' };
                            }
                        }
                        return {};
                    }}                    
                    extraActions={this.renderRestaurantsActions()}
                    extraInfo={this.renderRestaurantsInfo()}
                    paginated
                   
                />

            </div>
        );
    }
}

Restaurants.propTypes = {
    restaurants: PropTypes.array,
    chains: PropTypes.array,
    editedType: PropTypes.string,
    deletedOnly: PropTypes.bool,
    setEditedEntity: PropTypes.func,
    fetchArchivedRestaurants: PropTypes.func,
    posSuppliers: PropTypes.array,
    confirmDelete: PropTypes.func,
    deleteRestaurant: PropTypes.func,
    history: PropTypes.object,
    match: PropTypes.object,
    
};

const mapStateToProps = (state, ownProps) => {
    const { chains } = state.businessApp;
    const restaurants = getFullRestaurantsData(state).filter(rest => ownProps.match.params.chainId ? rest.ownerId === ownProps.match.params.chainId : rest) || {};
    const posSuppliers = state.meta.posSuppliers;
    const { type: editedType } = state.editor;

    return { restaurants, chains, editedType, posSuppliers };
};

export default connect(mapStateToProps,
    { setEditedEntity, confirmDelete, deleteRestaurant, fetchArchivedRestaurants })(Restaurants);
